import Fuse from "fuse.js";
import { sortBy } from "lodash-es";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import type { ConnectedProjectDto } from "@/api/types";
import { Briefcase02Icon } from "@/components/Icons/Briefcase02Icon";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { getProjectSwitchHref } from "@/helpers/getProjectSwitchHref";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import { RuntimeHeight } from "@/ui-adapters/RuntimeHeight";

import { ProjectItem } from "./ProjectItem";

interface ProjectListProps {
  projects: ConnectedProjectDto[];
  recentProjects?: ConnectedProjectDto[];
  currentProjectId?: string;
  showCurrentProject?: boolean;
  showRecentProjects?: boolean;
  showPortfolio?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  maxProjects?: number;
  autoFocusSearchInput?: boolean;
  searchQuery?: string;
  onSearchQueryChange?: (searchQuery: string) => void;
}
export function ProjectList({
  projects,
  recentProjects,
  currentProjectId,
  showCurrentProject,
  showRecentProjects,
  showPortfolio,
  isLoading,
  onClose,
  maxProjects,
  autoFocusSearchInput,
  searchQuery: controlledSearchQuery,
  onSearchQueryChange,
}: ProjectListProps): React.ReactNode {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [uncontrolledSearchQuery, setUncontrolledSearchQuery] = useState("");
  const searchQuery = controlledSearchQuery ?? uncontrolledSearchQuery;
  const updateSearchQuery = (query: string) => {
    if (controlledSearchQuery === undefined) {
      setUncontrolledSearchQuery(query);
    }

    onSearchQueryChange?.(query);
  };
  const projectCandidateList = useMemo(
    () => projects.filter((p) => showCurrentProject || p.id !== currentProjectId),
    [currentProjectId, projects, showCurrentProject],
  );

  const hasSearchQuery = searchQuery.trim().length > 0;
  const fuse = useMemo(
    () =>
      hasSearchQuery
        ? new Fuse<ConnectedProjectDto>(projectCandidateList, {
            keys: [
              { name: "name", weight: 2 },
              { name: "city", weight: 1 },
            ],
            threshold: 0.35,
            shouldSort: true,
          })
        : undefined,
    [hasSearchQuery, projectCandidateList],
  );

  const filteredList = useMemo(() => {
    const searchTerm = searchQuery?.trim();

    if (!searchTerm || !fuse) {
      if (showRecentProjects && recentProjects) {
        const recentProjectIds = new Set(recentProjects.map((x) => x.id));

        return sortBy(
          projectCandidateList.filter((p) => !recentProjectIds.has(p.id)),
          (x) => x.name.toUpperCase(),
        );
      } else {
        return sortBy(projectCandidateList, (x) => x.name.toUpperCase());
      }
    }

    return fuse.search(searchTerm).map((r) => r.item);
  }, [projectCandidateList, fuse, recentProjects, showRecentProjects, searchQuery]);
  const showRecent = showRecentProjects && !hasSearchQuery && !!recentProjects?.length;
  const showOther = !!filteredList.length;
  const showNoResultsFound = hasSearchQuery && !filteredList.length;

  return (
    <section className="flex min-h-0 flex-col gap-space-sm overflow-auto p-space-md">
      {projects.length > 3 && (
        <SearchInput
          width="full"
          value={searchQuery}
          placeholder={t("component.project-switcher.search.placeholder")}
          name="project-switcher-search"
          aria-label={t("component.project-switcher.search.placeholder")}
          onChange={(e) => updateSearchQuery(e.target.value || "")}
          onKeyDown={async (e) => {
            if (e.key === "Enter" && hasSearchQuery && filteredList.length > 0) {
              await navigate(getProjectSwitchHref(window.location.pathname, filteredList[0].slug));
              onClose?.();
            }
          }}
          autoFocus={autoFocusSearchInput}
        />
      )}
      {isLoading ? (
        t("component.project-switcher.loading")
      ) : (
        <RuntimeHeight maxHeight={maxProjects ? maxProjects * 72 : "auto"}>
          {(runtimeHeightAttributes) => (
            <div {...runtimeHeightAttributes} id="project-switcher-project-list" className="overflow-auto pb-space-lg">
              {showPortfolio && !searchQuery && (
                <>
                  <Link
                    className="flex w-full button-press items-center gap-space-md rounded-md p-space-sm transition-colors hover:bg-action-subtle-primary-hover focus:outline-hidden focus-visible:ring-1 focus-visible:ring-border-default focus-visible:ring-inset pressed:bg-action-soft-primary"
                    to={routes.portfolio.overview()}
                    onClick={onClose}
                  >
                    <span className="flex size-thumbnail-compact items-center justify-center">
                      <Briefcase02Icon size={24} tone="secondary" />
                    </span>
                    <span className="flex flex-1 flex-col items-start">
                      <span className="line-clamp-1 text-left text-body-emphasis break-all">
                        {t("component.project-switcher.portfolio")}
                      </span>
                      <span className="text-overline text-text-secondary">
                        {t("component.project-switcher.portfolio.subtitle")}
                      </span>
                    </span>
                  </Link>
                  {showRecent || showOther ? <hr className="my-space-md h-px border-0 bg-background-tertiary" /> : null}
                </>
              )}
              {showRecent && (
                <ul className="flex flex-col">
                  {recentProjects.map((p) => (
                    <li key={p.id}>
                      <ProjectItem project={p} onClick={onClose} />
                    </li>
                  ))}
                </ul>
              )}
              {showRecent && showOther && <hr className="my-space-md h-px border-0 bg-background-tertiary" />}
              {showOther && (
                <ul className="flex flex-col">
                  {filteredList.map((p) => (
                    <li key={p.id}>
                      <ProjectItem project={p} onClick={onClose} />
                    </li>
                  ))}
                </ul>
              )}
              {showNoResultsFound && (
                <p className="mt-space-md text-caption text-text-secondary">
                  {t("component.project-switcher.search.no-results")}
                </p>
              )}
            </div>
          )}
        </RuntimeHeight>
      )}
    </section>
  );
}
